// Copyright (c) 2022 by Move Closer
import { VueConstructor } from 'vue'
import { toRef, SetupContext, ref } from '@vue/composition-api'

import { ComponentObjectPropsOptions } from '../../vue-api'
import { FormControlModelType } from '../../abstract/general'
import { getAbstractInputControlProps } from '../../abstract/components/InputControl/InputControl.hooks'
import { useErrors, useSyncModel } from '../../composables'

import { PasswordInputProps, UsePasswordInputProvides } from './PasswordInput.contracts'
import CloseEyeIcon from './icons/CloseEyeIcon.vue'
import OpenEyeIcon from './icons/OpenEyeIcon.vue'

/**
 * @author Jan Dobrowolski <jan.dobrowolski@movecloser.pl> (original)
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl> (edited)
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (edited)
 */
export const passwordInputProps: ComponentObjectPropsOptions<PasswordInputProps> = {
  ...getAbstractInputControlProps<FormControlModelType>(),

  /**
   * @see PasswordInputProps.isToggleable
   */
  isToggleable: {
    type: Boolean,
    required: false,
    default: true
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (edited)
 */
export const usePasswordInput = (props: PasswordInputProps, ctx: SetupContext): UsePasswordInputProvides => {
  const { clearErrors, hasErrors } = useErrors(toRef(props, 'errors'), ctx.emit)

  const value = useSyncModel(toRef(props, 'model'), ctx.emit, clearErrors)

  /**
   * @see UsePasswordInputProvides.inputType
   */
  const inputType = ref<string>('password')
  /**
   * @see UsePasswordInputProvides.inputType
   */
  const icon = ref<VueConstructor>(OpenEyeIcon as VueConstructor)

  /**
   * @see UsePasswordInputProvides.togglePasswordVisibility
   */
  const togglePasswordVisibility = (): void => {
    if (inputType.value === 'password') {
      inputType.value = 'text'
      icon.value = CloseEyeIcon as VueConstructor
    } else {
      inputType.value = 'password'
      icon.value = OpenEyeIcon as VueConstructor
    }
  }

  return {
    hasErrors,
    inputType,
    icon,
    togglePasswordVisibility,
    value
  }
}
